import React from 'react'
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react'
import { stripeIdentityStart, stripeIdentityStatus } from '../api'

export type StripeIdentitySnapshot = {
  status: string
  verified: boolean
  feeDisplay: string
  feeStatement: string
}

type Props = {
  enabled?: boolean
  countryCode?: string | null
  returnTo?: string
  mode?: 'registration' | 'account'
  className?: string
  onChange?: (snapshot: StripeIdentitySnapshot) => void
}

const STRIPE_IDENTITY_FEE_MULTIPLIER = 2.5
const STRIPE_IDENTITY_STRIPE_COST_CENTS_BY_COUNTRY: Record<string, number> = {
  AU: 200,
  NZ: 200,
  US: 150,
  CA: 200,
  GB: 125,
  IE: 125,
}
const CURRENCY_BY_COUNTRY: Record<string, string> = {
  AU: 'AUD',
  NZ: 'NZD',
  US: 'USD',
  CA: 'CAD',
  GB: 'GBP',
  IE: 'EUR',
  SG: 'SGD',
  HK: 'HKD',
}

const normalizeCountryCode = (value?: string | null) => {
  const code = String(value || '').trim().toUpperCase()
  return code || 'AU'
}

const defaultFeeDisplayForCountry = (countryCode?: string | null) => {
  const code = normalizeCountryCode(countryCode)
  const currency = CURRENCY_BY_COUNTRY[code] || CURRENCY_BY_COUNTRY.AU
  const baseCents =
    STRIPE_IDENTITY_STRIPE_COST_CENTS_BY_COUNTRY[code] ||
    STRIPE_IDENTITY_STRIPE_COST_CENTS_BY_COUNTRY.AU
  const amountCents = Math.round(baseCents * STRIPE_IDENTITY_FEE_MULTIPLIER)
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amountCents / 100)
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency}`
  }
}

const defaultSnapshotForCountry = (countryCode?: string | null): StripeIdentitySnapshot => ({
  status: 'not_started',
  verified: false,
  feeDisplay: defaultFeeDisplayForCountry(countryCode),
  feeStatement: 'Includes Stripe verification and processing fees.',
})

const normalizeStatus = (value?: string | null) => {
  const s = String(value || '').trim().toLowerCase()
  return s || 'not_started'
}

const statusLabel = (status: string, verified: boolean) => {
  if (verified) return 'Verified by Stripe'
  if (status === 'checkout_open') return 'Checkout started, payment not completed'
  if (status === 'fee_pending') return 'Fee paid, verification pending'
  if (status === 'pending') return 'Verification in progress'
  if (status === 'requires_input') return 'More information required by Stripe'
  if (status === 'failed') return 'Verification incomplete'
  return 'Not verified yet'
}

const fallbackReturnPathForMode = (mode: 'registration' | 'account') => {
  if (typeof window === 'undefined') return '/account'
  if (mode === 'account') return '/account'

  const path = String(window.location.pathname || '').startsWith('/')
    ? String(window.location.pathname || '')
    : '/account'
  const params = new URLSearchParams(window.location.search || '')
  params.delete('stripeIdentity')
  const query = params.toString()
  return `${path}${query ? `?${query}` : ''}`
}

export default function StripeIdentityVerifyPanel({
  enabled = true,
  countryCode,
  returnTo,
  mode = 'registration',
  className,
  onChange,
}: Props) {
  const [loading, setLoading] = React.useState(false)
  const [starting, setStarting] = React.useState(false)
  const [error, setError] = React.useState('')
  const [snapshot, setSnapshot] = React.useState<StripeIdentitySnapshot>(() =>
    defaultSnapshotForCountry(countryCode)
  )

  const refresh = React.useCallback(async () => {
    const fallbackSnapshot = defaultSnapshotForCountry(countryCode)
    if (!enabled) {
      setSnapshot(fallbackSnapshot)
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await stripeIdentityStatus(countryCode || undefined)
      const next: StripeIdentitySnapshot = {
        status: normalizeStatus(res?.status),
        verified: Boolean(res?.verified),
        feeDisplay: String(res?.fee?.display || fallbackSnapshot.feeDisplay),
        feeStatement: String(res?.fee?.statement || fallbackSnapshot.feeStatement),
      }
      setSnapshot(next)
    } catch (e: any) {
      setError(e?.message || 'Unable to load Stripe verification status')
    } finally {
      setLoading(false)
    }
  }, [enabled, countryCode])

  React.useEffect(() => {
    refresh()
  }, [refresh])

  React.useEffect(() => {
    onChange?.(snapshot)
  }, [onChange, snapshot])

  const beginStripeVerification = React.useCallback(async () => {
    if (!enabled || starting) return
    setStarting(true)
    setError('')
    try {
      const fallbackReturnTo = fallbackReturnPathForMode(mode)
      const res = await stripeIdentityStart({
        returnTo: returnTo || fallbackReturnTo,
        countryCode: countryCode || undefined,
      })
      const url = String(res?.url || '').trim()
      if (!url) throw new Error('Stripe checkout URL was not returned')
      window.location.assign(url)
    } catch (e: any) {
      setError(e?.message || 'Unable to start Stripe verification')
      setStarting(false)
      await refresh()
    }
  }, [enabled, starting, returnTo, countryCode, mode, refresh])

  const status = normalizeStatus(snapshot.status)
  const verified = snapshot.verified
  const hasCardClass = String(className || '').includes('identity-choice-card')
  const headingText =
    mode === 'account'
      ? 'Optional: Verify with Stripe for ID verified badge'
      : 'Option A: Verify with Stripe'

  return (
    <div
      className={className}
      style={{
        marginTop: hasCardClass ? 0 : 12,
        padding: '12px 14px',
        borderRadius: 12,
        border: verified ? '1px solid rgba(22,163,74,0.35)' : '1px solid rgba(15,118,110,0.24)',
        background: verified ? '#ecfdf3' : '#f0fdfa',
      }}
    >
      <div className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ShieldCheck size={16} />
        {headingText}
      </div>
      <p className="hint" style={{ margin: '6px 0 0' }}>
        Fee: <strong>{snapshot.feeDisplay}</strong>. {snapshot.feeStatement}
      </p>
      <p className="hint" style={{ margin: '6px 0 0' }}>
        Status: {statusLabel(status, verified)}
      </p>
      <div className="inline-gap-2" style={{ marginTop: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn-outline"
          onClick={beginStripeVerification}
          disabled={!enabled || starting || loading}
        >
          {starting ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Loader2 size={15} />
              Redirecting…
            </span>
          ) : status === 'checkout_open' ? (
            'Complete Stripe payment'
          ) : status === 'fee_pending' || status === 'pending' || status === 'requires_input' ? (
            'Continue Stripe verification'
          ) : verified ? (
            'Re-run Stripe verification'
          ) : (
            'Verify with Stripe'
          )}
        </button>
        {verified && (
          <span
            className="chip"
            style={{ background: '#dcfce7', borderColor: '#16a34a', color: '#166534' }}
          >
            <CheckCircle2 size={14} style={{ marginRight: 6 }} />
            Stripe ID verified badge active
          </span>
        )}
      </div>
      {loading && <p className="hint" style={{ marginTop: 8 }}>Checking Stripe status…</p>}
      {error && (
        <p className="hint" style={{ marginTop: 8, color: '#b91c1c' }}>
          {error}
        </p>
      )}
    </div>
  )
}
